import type { CSSProperties } from "react";

export type DrawingUnit = "px" | "mm" | "cm" | "m" | "in" | "ft";
export type DecimalSeparator = "comma" | "point";
export type DimensionMarkerStyle = "filled-arrow" | "slash" | "dot";
export type LineType = "solid" | "dashed" | "dashed-x2" | "dash-dot" | "dash-dot-x2" | "dot-dot" | "dot-dot-x2";
export type RoofComponentLabelPosition = "bottom" | "middle" | "top";
export type VertexShape = "circle" | "square" | "cross";
export type SettingsPage = "units" | "general" | "shortcuts" | "entities";

export const LINE_TYPE_OPTIONS = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dashed-x2", label: "Dashed x2" },
  { value: "dash-dot", label: "Dash-Dot" },
  { value: "dash-dot-x2", label: "Dash-Dot x2" },
  { value: "dot-dot", label: "Dot-Dot" },
  { value: "dot-dot-x2", label: "Dot-Dot x2" },
] as const satisfies ReadonlyArray<{ value: LineType; label: string }>;

export interface SafetySystemSettings {
  vertexShape: VertexShape;
  strokeWidth: number;
  lineType: LineType;
  maxSpacing: number;
  maxSingleLineLength: number;
  maxCornersPerLifeline: number;
  chamferAcuteAngles: boolean;
  chamferSegmentLength: number;
  intermediateAngleMax: number;
  rectCurveMinAngle: number;
  rectCurveMaxAngle: number;
  bendCurveMinAngle: number;
  bendCurveMaxAngle: number;
}

export interface EntityTypeStyle {
  strokeColor: string;
  strokeWidth: number;
  lineType: LineType;
  vertexColor: string;
  vertexSize: number;
  vertexShape: VertexShape;
  gripColor: string;
  gripSize: number;
  labelText: string;
  labelHeight: number;
  labelPosition: RoofComponentLabelPosition;
}

export interface SafetySystemOption {
  value: string;
  label: string;
  icon: string;
  description: string;
}

export interface ShortcutOption {
  command: string;
  label: string;
}

export interface EntityStyleOption {
  kind: string;
  label: string;
}

export interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  panelStyle?: CSSProperties;
  onActivate?: () => void;
  drawingUnit: DrawingUnit;
  onDrawingUnitChange: (value: DrawingUnit) => void;
  decimalSeparator: DecimalSeparator;
  onDecimalSeparatorChange: (value: DecimalSeparator) => void;
  gridSpacingDraft: string;
  onGridSpacingDraftChange: (value: string) => void;
  commitGridSpacingDraft: () => void;
  isGridSnapEnabled: boolean;
  onGridSnapEnabledChange: (value: boolean) => void;
  gridSnapStepDraft: string;
  onGridSnapStepDraftChange: (value: string) => void;
  commitGridSnapStepDraft: () => void;
  osnapTypes: string[];
  enabledOsnaps: Record<string, boolean>;
  onOsnapToggle: (type: string, value: boolean) => void;
  entityLineWidth: number;
  onEntityLineWidthChange: (value: number) => void;
  previewLineWidth: number;
  onPreviewLineWidthChange: (value: number) => void;
  dimensionTextHeightDraft: string;
  onDimensionTextHeightDraftChange: (value: string) => void;
  commitDimensionTextHeightDraft: () => void;
  dimensionTextGapDraft: string;
  onDimensionTextGapDraftChange: (value: string) => void;
  commitDimensionTextGapDraft: () => void;
  dimensionMarkerStyle: DimensionMarkerStyle;
  onDimensionMarkerStyleChange: (value: DimensionMarkerStyle) => void;
  safetySystemOptions: SafetySystemOption[];
  selectedSafetySystem: string;
  onSelectedSafetySystemChange: (value: string) => void;
  safetySystemSettings: Record<string, SafetySystemSettings>;
  updateSafetySystemSetting: (
    system: string,
    field: keyof SafetySystemSettings,
    value: SafetySystemSettings[keyof SafetySystemSettings],
  ) => void;
  shortcutOptions: ShortcutOption[];
  keyboardShortcuts: Record<string, string>;
  onKeyboardShortcutChange: (command: string, value: string) => void;
  entityStyleOptions: EntityStyleOption[];
  entityTypeStyles: Record<string, EntityTypeStyle>;
  updateEntityTypeStyle: (
    kind: string,
    field: keyof EntityTypeStyle,
    value: EntityTypeStyle[keyof EntityTypeStyle],
  ) => void;
}
